
import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import defaultLogo from "../../../../assets/img/auth/logo.jpg";
import { API_BASE_URL } from "../../../../config";

// ✅ Helper to format date for datetime-local input
const formatDateTimeLocal = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const EditBillForm = ({ bill, customer, project, onClose, onBillUpdated }) => {
  const [ratePerSqft, setRatePerSqft] = useState(bill.ratePerSqft || "");
  const [areaSqft, setAreaSqft] = useState(bill.areaSqft || "");
  const [basePrice, setBasePrice] = useState(bill.basePrice || "");
  const [amenitiesCharge, setAmenitiesCharge] = useState(
    bill.amenitiesCharge || ""
  );
  const [parking, setParking] = useState(
    bill.parking?.total || "" // backend me object store hai
  );
  const [extraCharges, setExtraCharges] = useState(bill.extraCharges || "");
  const [discount, setDiscount] = useState(bill.discount || "");
  const [taxPercent, setTaxPercent] = useState(bill.taxPercent || "");
  const [totalAmount, setTotalAmount] = useState(bill.totalAmount || 0);
  const [remarks, setRemarks] = useState(bill.remarks || "");

  const [amountValidTill, setAmountValidTill] = useState(
    formatDateTimeLocal(bill.amountValidTill)
  );

  const [logoUrl, setLogoUrl] = useState(defaultLogo);
  const [companyName] = useState("Swaramayi Real Estate Marketing");
  const token = localStorage.getItem("token");

  // 🔹 Fetch company logo
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/admin/get-companylogo`, {
        headers: { token },
      })
      .then((res) => {
        const photoPath = res.data?.result?.profilePhoto;
        if (photoPath) {
          let finalPath = photoPath.startsWith("http")
            ? photoPath
            : `${API_BASE_URL}${
                photoPath.startsWith("/") ? photoPath : `/${photoPath}`
              }`;
          setLogoUrl(finalPath);
        } else {
          setLogoUrl(defaultLogo);
        }
      })
      .catch(() => setLogoUrl(defaultLogo));
  }, [token]);

  // 🔹 Auto calculation
  useEffect(() => {
    // Base Price = Rate * Area
    if (ratePerSqft && areaSqft) {
      const calculatedBase = Number(ratePerSqft) * Number(areaSqft);
      setBasePrice(calculatedBase.toFixed(2));
    }

    let subtotal =
      (Number(basePrice) || 0) +
      (Number(amenitiesCharge) || 0) +
      (Number(parking) || 0) +
      (Number(extraCharges) || 0) -
      (Number(discount) || 0);

    let taxAmount = (subtotal * (Number(taxPercent) || 0)) / 100;

    let total = subtotal + taxAmount;

    if (!isNaN(total)) setTotalAmount(total.toFixed(2));
  }, [
    ratePerSqft,
    areaSqft,
    basePrice,
    amenitiesCharge,
    parking,
    extraCharges,
    discount,
    taxPercent,
  ]);

  // 🔹 Update API
  const handleUpdate = async () => {
    if (!ratePerSqft || !areaSqft || !basePrice) {
      toast.error("Please fill required fields!");
      return;
    }
    if (!amountValidTill) {
      toast.error("Please select bill validity date & time");
      return;
    }

    let parkingData = parking
      ? {
          type: "General",
          count: 1,
          rate: Number(parking),
          total: Number(parking),
        }
      : null;

    try {
      const payload = {
        ratePerSqft: Number(ratePerSqft),
        areaSqft: Number(areaSqft),
        basePrice: Number(basePrice),
        amenitiesCharge: Number(amenitiesCharge),
        parking: parkingData,
        extraCharges: Number(extraCharges),
        discount: Number(discount),
        taxPercent: Number(taxPercent),
        totalAmount: Number(totalAmount),
        remarks,
        amountValidTill: new Date(amountValidTill).toISOString(),
      };

      const res = await axios.post(
        `${API_BASE_URL}/api/billRecord/edit-bill/${bill._id}`,
        payload,
        { headers: { token } }
      );

      if (res.data.success) {
        toast.success("Bill updated successfully!");
        if (typeof onBillUpdated === "function") {
          onBillUpdated(res.data.data);
        }
        onClose();
      } else {
        toast.error(res.data.message || "Failed to update bill");
      }
    } catch (err) {
      // console.error("Edit bill error:", err.response?.data || err.message);
      toast.error(err.response?.data?.message || "Error updating bill");
    }
  };

  return (
    <div className="bg-black/30 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-5">
          <div className="flex items-center gap-4">
            <img
              src={logoUrl}
              alt="Company Logo"
              onError={() => setLogoUrl(defaultLogo)}
              className="h-16 w-16 object-contain"
            />
            <h1 className="text-2xl font-bold">{companyName}</h1>
          </div>
          <button
            onClick={onClose}
            className="rounded bg-red-700 px-2 py-2 text-white hover:bg-red-800"
          >
            ✖
          </button>
        </div>

        <div className="space-y-6 p-6">
          {/* Customer & Project Info */}
          <div className="grid grid-cols-2 gap-6 rounded-lg border bg-gray-50 p-4">
            <div>
              <h3 className="mb-3 text-lg font-semibold">Customer Details</h3>
              <p>
                <strong>Name:</strong> {customer.customerName || "-"}
              </p>
              <p>
                <strong>Phone:</strong> {customer.customerNumber || "-"}
              </p>
              <p>
                <strong>Location:</strong> {customer.locationRequired || "-"}
              </p>
            </div>
            <div>
              <h3 className="mb-3 text-lg font-semibold">Project Details</h3>
              <p>
                <strong>Project:</strong> {project.projectName || "-"}
              </p>
              <p>
                <strong>Type:</strong> {project.projectType || "-"}
              </p>
              <p>
                <strong>Flat Type:</strong> {project.flatType || "-"}
              </p>
              <p>
                <strong>Flat Facing:</strong> {project.flatFacing || "-"}
              </p>
              <p>
                <strong>Amenities:</strong> {project.amenities || "-"}
              </p>
              <p>
                <strong>Address:</strong> {project.projectAddress || "-"}
              </p>
            </div>
          </div>

          {/* Bill Form */}
          <div className="space-y-4 rounded-lg border p-4">
            <h3 className="mb-2 text-lg font-semibold">Cost Sheet Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block font-medium">Valid Till</label>
                <input
                  type="datetime-local"
                  value={amountValidTill}
                  onChange={(e) => setAmountValidTill(e.target.value)}
                  className="w-full rounded border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Rate/Sqft</label>
                <input
                  type="number"
                  value={ratePerSqft}
                  onChange={(e) => setRatePerSqft(e.target.value)}
                  className="w-full rounded border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Area (Sqft)</label>
                <input
                  type="number"
                  value={areaSqft}
                  onChange={(e) => setAreaSqft(e.target.value)}
                  className="w-full rounded border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Base Price</label>
                <input
                  type="number"
                  value={basePrice}
                  readOnly
                  className="w-full rounded border bg-gray-100 p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Amenities Charge</label>
                <input
                  type="number"
                  value={amenitiesCharge}
                  onChange={(e) => setAmenitiesCharge(e.target.value)}
                  className="w-full rounded border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Parking</label>
                <input
                  type="number"
                  value={parking}
                  onChange={(e) => setParking(e.target.value)}
                  className="w-full rounded border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Extra Charges</label>
                <input
                  type="number"
                  value={extraCharges}
                  onChange={(e) => setExtraCharges(e.target.value)}
                  className="w-full rounded border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Discount</label>
                <input
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="w-full rounded border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Tax %</label>
                <input
                  type="number"
                  value={taxPercent}
                  onChange={(e) => setTaxPercent(e.target.value)}
                  className="w-full rounded border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">Total Amount</label>
                <input
                  type="text"
                  value={Number(totalAmount).toFixed(2)}
                  readOnly
                  className="w-full rounded border bg-gray-100 p-2"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block font-medium">Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="w-full rounded border p-2"
                rows="3"
              ></textarea>
            </div>
          </div>

          {/* Notes */}
          <div className="rounded border border-yellow-300 bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This cost sheet is valid for <b>7 days</b>{" "}
              only.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="rounded border px-5 py-2 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              className="rounded bg-green-600 px-5 py-2 text-white hover:bg-green-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBillForm;
